/**
 * Color Converter Utility
 * Provides methods to convert between RGB, HSL, and HEX formats
 */

window.ColorConverter = {
  utils: {
    /**
     * Converts a hue value to a pastel color using fixed saturation (70%) and lightness (80%)
     * @param {number} hue - Hue value (0-360)
     * @returns {string} Hexadecimal color code for a pastel color
     */
    hueToPastel(hue) {
      // Normalize hue to 0-360 range
      hue = ((hue % 360) + 360) % 360;
      
      // Use fixed saturation and lightness for pastel effect
      const saturation = 70;
      const lightness = 80;
      
      return ColorConverter.hsl.toHex(hue, saturation, lightness);
    },

    /**
     * Converts a hue value to a soft pastel color (lower saturation)
     * @param {number} hue - Hue value (0-360)
     * @returns {string} Hexadecimal color code for a soft pastel color
     */
    hueToSoftPastel(hue) {
      hue = ((hue % 360) + 360) % 360;
      return ColorConverter.hsl.toHex(hue, 45, 85); // Lower saturation, higher lightness
    },

    /**
     * Converts a hue value to a vibrant pastel color (higher saturation)
     * @param {number} hue - Hue value (0-360)
     * @returns {string} Hexadecimal color code for a vibrant pastel color
     */
    hueToVibrantPastel(hue) {
      hue = ((hue % 360) + 360) % 360;
      return ColorConverter.hsl.toHex(hue, 85, 75); // Higher saturation, lower lightness
    },

    /**
     * Converts a hue value to a dusty pastel color (muted effect)
     * @param {number} hue - Hue value (0-360)
     * @returns {string} Hexadecimal color code for a dusty pastel color
     */
    hueToDustyPastel(hue) {
      hue = ((hue % 360) + 360) % 360;
      return ColorConverter.hsl.toHex(hue, 35, 75); // Low saturation, medium lightness
    },

    /** 
     * Converts a hue value to a dark pastel color (muted effect)
     * @param {number} hue - Hue value (0-360)
     * @returns {string} Hexadecimal color code for a dark pastel color
     */
    hueToDarkPastel(hue) {
      hue = ((hue % 360) + 360) % 360;
      return ColorConverter.hsl.toHex(hue, 35, 10); // Low saturation, medium lightness
    },

    /**
     * Generates a complementary pastel color pair
     * @param {number} hue - Base hue value (0-360)
     * @returns {Object} Object containing two complementary pastel colors
     */
    getPastelPair(hue) {
      hue = ((hue % 360) + 360) % 360;
      const complementaryHue = (hue + 180) % 360;
      
      return {
        base: this.hueToPastel(hue),
        complement: this.hueToPastel(complementaryHue)
      };
    },

    /**
     * Converts a hue value to a tag color
     * @param {number} hue - Hue value (0-360)
     * @returns {string} Hexadecimal color code for a tag color
     */
    hueToTag(hue) {
        hue = ((hue % 360) + 360) % 360;
        return ColorConverter.hsl.toHex(hue, 72, 12);
    },

    /**
     * Generates a pastel color palette with different variations of the same hue
     * @param {number} hue - Base hue value (0-360)
     * @returns {Object} Object containing different pastel variations
     */
    getPastelPalette(hue) {
      hue = ((hue % 360) + 360) % 360;
      
      return {
        standard: this.hueToPastel(hue),
        soft: this.hueToSoftPastel(hue),
        vibrant: this.hueToVibrantPastel(hue),
        dusty: this.hueToDustyPastel(hue),
        dark: this.hueToDarkPastel(hue),
        tag: this.hueToTag(hue)
      };
    }
  },

  rgb: {
    /**
     * Converts RGB color values to HSL
     * @param {number} r - Red value (0-255)
     * @param {number} g - Green value (0-255)
     * @param {number} b - Blue value (0-255)
     * @returns {Object} HSL values { h: 0-360, s: 0-100, l: 0-100 }
     */
    toHsl(r, g, b) {
      // Convert RGB values to 0-1 range
      r /= 255;
      g /= 255;
      b /= 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;

      if (max === min) {
        // Achromatic (gray)
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        // Calculate hue
        switch (max) {
          case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
        }
        h /= 6;
      }

      // Convert to degrees and percentages
      return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100)
      };
    },

    /**
     * Converts RGB color values to hexadecimal
     * @param {number} r - Red value (0-255)
     * @param {number} g - Green value (0-255)
     * @param {number} b - Blue value (0-255)
     * @returns {string} Hexadecimal color code (e.g., "#FF0000")
     */
    toHex(r, g, b) {
      const toHex = (n) => {
        const hex = Math.round(n).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      };

      return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
    }
  },

  hsl: {
    /**
     * Converts HSL color values to RGB
     * @param {number} h - Hue value (0-360)
     * @param {number} s - Saturation value (0-100)
     * @param {number} l - Lightness value (0-100)
     * @returns {Object} RGB values { r: 0-255, g: 0-255, b: 0-255 }
     */
    toRgb(h, s, l) {
      // Convert to 0-1 range
      h /= 360;
      s /= 100;
      l /= 100;

      let r, g, b;

      if (s === 0) {
        // Achromatic (gray)
        r = g = b = l;
      } else {
        const hue2rgb = (p, q, t) => {
          if (t < 0) t += 1;
          if (t > 1) t -= 1;
          if (t < 1/6) return p + (q - p) * 6 * t;
          if (t < 1/2) return q;
          if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
          return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;

        r = hue2rgb(p, q, h + 1/3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1/3);
      }

      // Convert to 0-255 range
      return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255)
      };
    },

    /**
     * Converts HSL color values to hexadecimal
     * @param {number} h - Hue value (0-360)
     * @param {number} s - Saturation value (0-100)
     * @param {number} l - Lightness value (0-100)
     * @returns {string} Hexadecimal color code (e.g., "#FF0000")
     */
    toHex(h, s, l) {
      const rgb = this.toRgb(h, s, l);
      return ColorConverter.rgb.toHex(rgb.r, rgb.g, rgb.b);
    }
  },

  hex: {
    /**
     * Converts a hexadecimal color code to RGB values
     * @param {string} hex - Hexadecimal color code (e.g., "#FF0000" or "FF0000")
     * @returns {Object} RGB values { r: 0-255, g: 0-255, b: 0-255 }
     * @throws {Error} If invalid hex color code is provided
     */
    toRgb(hex) {
      // Remove # if present
      hex = hex.replace(/^#/, '');

      // Validate hex format
      if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
        throw new Error('Invalid hex color format. Use format: #RRGGBB or RRGGBB');
      }

      // Parse the hex values
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      return { r, g, b };
    },

    /**
     * Converts a hexadecimal color code to HSL values
     * @param {string} hex - Hexadecimal color code (e.g., "#FF0000" or "FF0000")
     * @returns {Object} HSL values { h: 0-360, s: 0-100, l: 0-100 }
     * @throws {Error} If invalid hex color code is provided
     */
    toHsl(hex) {
      const rgb = this.toRgb(hex);
      return ColorConverter.rgb.toHsl(rgb.r, rgb.g, rgb.b);
    },

    /**
     * Converts a hexadecimal color code to its hue value
     * @param {string} hex - Hexadecimal color code (e.g., "#FF0000" or "FF0000")
     * @returns {number} Hue value (0-360)
     * @throws {Error} If invalid hex color code is provided
     */
    toHue(hex) {
      const hsl = this.toHsl(hex);
      return hsl.h;
    }
  },

  pantone: {
    /**
     * Common Pantone color mappings to hex values
     * This is a subset of common Pantone colors. For a complete list,
     * please refer to official Pantone documentation
     */
    colorMap: {
      // Reds
      'PANTONE 185 C': '#E4002B',
      'PANTONE 186 C': '#C8102E',
      'PANTONE 187 C': '#A6192E',
      'PANTONE RED 032 C': '#EF3340',
      
      // Blues
      'PANTONE 286 C': '#0032A0',
      'PANTONE 287 C': '#003087',
      'PANTONE 288 C': '#002D72',
      'PANTONE 2925 C': '#009CDE',
      
      // Greens
      'PANTONE 347 C': '#009A44',
      'PANTONE 348 C': '#00843D',
      'PANTONE 349 C': '#006838',
      'PANTONE 354 C': '#00B140',
      
      // Yellows
      'PANTONE 012 C': '#FFD700',
      'PANTONE 116 C': '#FFCD00',
      'PANTONE 7548 C': '#FFC600',
      'PANTONE 108 C': '#FEDB00',
      
      // Oranges
      'PANTONE 151 C': '#FF8200',
      'PANTONE 1585 C': '#FF6A13',
      'PANTONE 021 C': '#FF5000',
      'PANTONE 165 C': '#FF5F00',
      
      // Purples
      'PANTONE 2587 C': '#7B4EA3',
      'PANTONE 2597 C': '#6C2E90',
      'PANTONE 2607 C': '#5F259F',
      'PANTONE 2617 C': '#522398',
      
      // Neutrals
      'PANTONE COOL GRAY 1 C': '#D9D9D6',
      'PANTONE COOL GRAY 5 C': '#B1B3B3',
      'PANTONE COOL GRAY 7 C': '#97999B',
      'PANTONE COOL GRAY 10 C': '#63666A',
      'PANTONE BLACK C': '#000000',
      
      // Metallics
      'PANTONE 871 C': '#84754E',
      'PANTONE 872 C': '#85714D',
      'PANTONE 873 C': '#866D4B',
      'PANTONE 874 C': '#8B6F4E',
      'PANTONE 875 C': '#8B6F4E',
      'PANTONE 876 C': '#8C6F4E'
    },

    /**
     * Converts a Pantone color code to hexadecimal
     * @param {string} pantoneCode - Pantone color code (e.g., "PANTONE 185 C")
     * @returns {string} Hexadecimal color code (e.g., "#E4002B")
     * @throws {Error} If Pantone color code is not found in the mapping
     */
    toHex(pantoneCode) {
      const normalizedCode = pantoneCode.toUpperCase().trim();
      const hexColor = this.colorMap[normalizedCode];
      
      if (!hexColor) {
        throw new Error(`Pantone color "${pantoneCode}" not found in the mapping. Please check the color code or refer to the colorMap for available colors.`);
      }
      
      return hexColor;
    },

    /**
     * Gets all available Pantone color codes in the mapping
     * @returns {string[]} Array of available Pantone color codes
     */
    getAvailableColors() {
      return Object.keys(this.colorMap);
    },

    /**
     * Finds the closest Pantone color to a given hex value
     * @param {string} hex - Hexadecimal color code to match
     * @returns {Object} Object containing the closest Pantone color code and its hex value
     */
    fromHex(hex) {
      // Normalize input hex
      const normalizedHex = hex.toUpperCase().replace(/^#/, '');
      
      // Convert input hex to RGB for comparison
      const inputRgb = ColorConverter.hex.toRgb(hex);
      
      let closestColor = null;
      let smallestDifference = Infinity;
      
      // Compare with each Pantone color
      Object.entries(this.colorMap).forEach(([pantoneCode, pantoneHex]) => {
        const pantoneRgb = ColorConverter.hex.toRgb(pantoneHex);
        
        // Calculate color difference using simple RGB distance
        const difference = Math.sqrt(
          Math.pow(inputRgb.r - pantoneRgb.r, 2) +
          Math.pow(inputRgb.g - pantoneRgb.g, 2) +
          Math.pow(inputRgb.b - pantoneRgb.b, 2)
        );
        
        if (difference < smallestDifference) {
          smallestDifference = difference;
          closestColor = {
            pantone: pantoneCode,
            hex: pantoneHex
          };
        }
      });
      
      return closestColor;
    }
  }
};

// Example usage:
// const hsl = ColorConverter.rgb.toHsl(255, 0, 0);     // Convert red to HSL
// const rgb = ColorConverter.hsl.toRgb(0, 100, 50);    // Convert red back to RGB
// const hexFromRgb = ColorConverter.rgb.toHex(255, 0, 0); // Convert RGB to Hex
// const hexFromHsl = ColorConverter.hsl.toHex(0, 100, 50); // Convert HSL to Hex
// const rgbFromHex = ColorConverter.hex.toRgb("#FF0000"); // Convert Hex to RGB
// const hslFromHex = ColorConverter.hex.toHsl("#FF0000"); // Convert Hex to HSL
// const hexFromPantone = ColorConverter.pantone.toHex("PANTONE 185 C"); // Convert Pantone to Hex
// const closestPantone = ColorConverter.pantone.fromHex("#E4002B"); // Find closest Pantone color
// const pastelColor = ColorConverter.utils.hueToPastel(180); // Convert hue to pastel color
// const softPastel = ColorConverter.utils.hueToSoftPastel(180); // Convert hue to soft pastel
// const vibrantPastel = ColorConverter.utils.hueToVibrantPastel(180); // Convert hue to vibrant pastel
// const dustyPastel = ColorConverter.utils.hueToDustyPastel(180); // Convert hue to dusty pastel
// const pastelPair = ColorConverter.utils.getPastelPair(180); // Get complementary pastel pair
// const palette = ColorConverter.utils.getPastelPalette(180); // Get full pastel palette 